import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (parts: { street: string; city: string; state: string; zip: string }) => void;
  placeholder?: string;
  id?: string;
}

let cachedKeyPromise: Promise<string> | null = null;
let scriptLoadingPromise: Promise<void> | null = null;

const getApiKey = () => {
  if (!cachedKeyPromise) {
    cachedKeyPromise = supabase.functions.invoke("get-maps-key").then(({ data, error }) => {
      if (error || !data?.key) throw error || new Error("No key");
      return data.key as string;
    });
  }
  return cachedKeyPromise;
};

const loadGoogleMaps = async () => {
  if ((window as any).google?.maps?.places) return;
  if (scriptLoadingPromise) return scriptLoadingPromise;
  const key = await getApiKey();
  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
  return scriptLoadingPromise;
};

export const AddressAutocomplete = ({ value, onChange, onPlaceSelected, placeholder, id }: AddressAutocompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) setReady(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current) return;
    const google = (window as any).google;
    if (!google?.maps?.places) return;
    const ac = new google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "us" },
      fields: ["address_components"],
      types: ["address"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const comps: any[] = place.address_components || [];
      const get = (type: string) => comps.find((c) => c.types.includes(type));
      const streetNum = get("street_number")?.long_name || "";
      const route = get("route")?.long_name || "";
      const city =
        get("locality")?.long_name ||
        get("sublocality")?.long_name ||
        get("postal_town")?.long_name ||
        "";
      const state = get("administrative_area_level_1")?.short_name || "";
      const zip = get("postal_code")?.long_name || "";
      const street = [streetNum, route].filter(Boolean).join(" ");
      onPlaceSelected({ street, city, state, zip });
    });
    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [ready, onPlaceSelected]);

  return (
    <Input
      ref={inputRef}
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete="off"
    />
  );
};
